import ApiService, { HttpMethod } from "../commons/api/api_service";
import BaseResponse from "../commons/api/base_response";
import { Endpoints } from "../commons/api/endpoints";
import { ViewControllerV2 } from "../commons/base/v2/view_controller";
import ViewModelV2 from "../commons/base/v2/view_model";
import Scaffold from "../commons/scaffold/scaffold";
import StringUtils from "../commons/utils/string_utils";

interface MenuSyncInfo {
    menuId: number;
    menuName: string;
    availablePOS: boolean;
    availableGoFood: boolean;
    availableGrabFood: boolean;
    availableShopeeFood: boolean;
    lastCheckTime: Date;
}

interface Outlet {
    id: number;
    name: string;
}

interface MenuSyncInfoResponse {
    [outletId: number]: MenuSyncInfo[]
}

interface GetOutletResponse {
    outletList: Outlet[]
}

function handleResponseStatusCode(status: number, VM: ViewModelV2) {
    switch (status) {
        case 400:
            VM.emit(() => {
                VM.snackBar = {
                    key: Date.now().toString(),
                    message: `400: Bad Request`,
                    type: "error",
                };
            })
            break;
        case 500:
            VM.emit(() => {
                VM.snackBar = {
                    key: Date.now().toString(),
                    message: `500: Internal Server Error`,
                    type: "error",
                };
            })
            break;
    }
}

class RunchiseMenuSyncDashboardVM extends ViewModelV2 {
    // Map<outletId, Outlet>
    outletData: Map<number, Outlet> = new Map();
    // Map<outletId, MenuSyncInfo>
    data: Map<number, MenuSyncInfo[]> = new Map();
    interval: NodeJS.Timer;

    constructor() {
        super();
        
        this.fetchOutletData().then(() => {
            this.fetchNewData();
        })
        .catch((_) => {});

        this.interval = setInterval(() => {
            this.fetchNewData();
        }, 30000);
    }

    setOutletDataStub() {
        this.outletData = new Map([
            [1, {id: 1, name: "Outlet A"}],
            [2, {id: 2, name: "Outlet B"}],
            [3, {id: 3, name: "Outlet C"}]
        ]);
    }

    fetchOutletData(): Promise<void> {
        return new Promise((resolve, reject) => {
            ApiService.fetch(
                Endpoints.getAllOutletWithId,
                {
                    httpMethod: HttpMethod.post,
                    onSuccess: (response?: BaseResponse<GetOutletResponse>) => {
                        if (!response) {
                            this.emit(() => {
                                this.snackBar = {
                                    key: Date.now().toString(),
                                    message: `Tidak dapat terhubung dengan server`,
                                    type: "error",
                                };
                            });
                            reject();
                            return;
                        }
                        
                        handleResponseStatusCode(response.code, this);
                        if (response.data) {
                            const outletDataMap: Map<number, Outlet> = new Map();
                            for (let data of response.data.outletList) {
                                outletDataMap.set(data.id, data);
                            }
                            this.emit(() => {
                                this.outletData = outletDataMap;
                            })
                            resolve();
                        } else {
                            this.emit(() => {
                                this.snackBar = {
                                    key: Date.now().toString(),
                                    message: `Tidak mendapatkan data dari server`,
                                    type: "error",
                                };
                            });
                            reject();
                        }
                    },
                    onFailure: (error, errorCode) => {
                        if (error) {
                            this.emit(() => {
                                this.snackBar = {
                                    key: Date.now().toString(),
                                    message: `${error.status}: ${error.message}`,
                                    type: "error",
                                };
                            });
                        }
                        reject();
                    }
                }
            )
        });
    }

    setNewDataStub() {
        this.emit(() => {
            const newData: MenuSyncInfo[] = [
                {
                    menuId: 1000,
                    menuName: "Menu Ini Enak Banget A",
                    availablePOS: true,
                    availableShopeeFood: false,
                    availableGrabFood: false,
                    availableGoFood: false,
                    lastCheckTime: new Date(),
                },
                {
                    menuId: 1100,
                    menuName: "Menu Ini Enak Banget B",
                    availablePOS: false,
                    availableShopeeFood: true,
                    availableGrabFood: true,
                    availableGoFood: true,
                    lastCheckTime: new Date(),
                },
                {
                    menuId: 1200,
                    menuName: "Menu Ini Enak Banget C",
                    availablePOS: true,
                    availableShopeeFood: false,
                    availableGrabFood: false,
                    availableGoFood: false,
                    lastCheckTime: new Date(),
                }
            ];

            this.data = new Map([
                [1, [newData[0], newData[1]]],
                [2, [newData[1]]],
                [3, [newData[2], newData[0], newData[1]]],
            ]);
        })
    }

    fetchNewData() {
        ApiService.fetch(
            Endpoints.getUnsyncMenus,
            {
                httpMethod: HttpMethod.post,
                onSuccess: (response: BaseResponse<MenuSyncInfoResponse>) => {
                    handleResponseStatusCode(response.code, this);
                    // console.log(response.data);
                    if (response.data) {
                        const jsonData = Object.entries(response.data);
                        const dataMap: Map<number, MenuSyncInfo[]> = new Map();
                        for (let [key, value] of jsonData) {
                            const outletId = parseInt(key);
                            const dataArr = value as MenuSyncInfo[];
                            dataMap.set(outletId, dataArr);
                        }
                        this.emit(() => {
                            this.data = dataMap
                        });
                    }
                },
                onFailure: (error, errorCode) => {
                    if (error) {
                        this.emit(() => {
                                this.snackBar = {
                                    key: Date.now().toString(),
                                    message: `${error.status}: ${error.message}`,
                                    type: "error",
                                };
                            });
                    }
                }
            }
        )
    }
}

interface RunchiseMenuSyncDashboardProps {}

const RunchiseMenuSyncDashboardVC = ViewControllerV2<RunchiseMenuSyncDashboardProps, RunchiseMenuSyncDashboardVM>();

/*
 * Dashboard is showing all menus that currently syncing and menus that are failed to sync in all attempts 
 */
export default class RunchiseMenuSyncDashboardScreen extends RunchiseMenuSyncDashboardVC {
    constructor(props: RunchiseMenuSyncDashboardProps) {
        super(props, new RunchiseMenuSyncDashboardVM())
    }

    generateTableBody(outletList: Map<number, Outlet>, data: Map<number, MenuSyncInfo[]>): JSX.Element[] {
        const result: JSX.Element[] = [];

        if (!data || data.size == 0) {
            return [];
        }

        const dataArr: MenuSyncInfo[] = [];

        let keyId = 0;
        for (let [key, values] of data.entries()) {
            for (let value of values) {                
                const outlet = outletList.get(key);
                result.push(
                    <tr key={keyId}>
                        <td className="border-l-2 border-r-2 border-b-2">{StringUtils.toTimeString(new Date(value.lastCheckTime))}</td>
                        <td className="border-r-2 border-b-2">{outlet?.name ?? "Unknown"}</td>
                        <td className="border-r-2 border-b-2 text-start pl-4">{value.menuName}</td>
                        <td className="border-r-2 border-b-2">
                            <input type="checkbox" checked={value.availablePOS} readOnly/>    
                        </td>
                        <td className="border-r-2 border-b-2">
                            <input type="checkbox" checked={value.availableShopeeFood} readOnly/>
                        </td>
                        <td className="border-r-2 border-b-2">
                            <input type="checkbox" checked={value.availableGrabFood} readOnly/>
                        </td>
                        <td className="border-r-2 border-b-2">
                            <input type="checkbox" checked={value.availableGoFood} readOnly/>
                        </td>
                    </tr>
                );
            }
        }

        return result;
    }

    build(): JSX.Element {
        const vm = this.viewModel;

        return (
            <Scaffold
                showSnackbar={vm.snackBar}
            >
                <div className="flex flex-col items-center mt-12">
                    <h1 className="font-bold text-base lg:text-2xl">Hasil Cek Berkala Menu Gagal Sync Dengan Runchise</h1>
                    <div className="w-full px-5 py-5 lg:px-10">
                        <table className="w-full table-fixed bg-gray-50 text-center font-bold text-[10px] lg:text-base">
                            <thead>
                                <tr className="border-t-2 border-l-2 border-r-2">
                                    <th className="border-r-2 w-[100px] lg:w-[200px]">Waktu Cek</th>
                                    <th className="border-r-2 w-[170px] lg:w-[200px]">Outlet</th>
                                    <th className="border-r-2">Nama Menu</th>
                                    <th colSpan={4} className="border-r-2 border-b-2 w-[250px] lg:w-[400px]">Status Ketersediaan Saat Dicek</th>
                                </tr>
                                <tr className="border-b-2 border-l-2 border-r-2">
                                    <th className="border-r-2"></th>
                                    <th className="border-r-2"></th>
                                    <th className="border-r-2"></th>
                                    <th className="border-r-2">POS</th>
                                    <th className="border-r-2">ShopeeFood</th>
                                    <th className="border-r-2">GrabFood</th>
                                    <th className="border-r-2">GoFood</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.generateTableBody(vm.outletData, vm.data)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Scaffold>
        )
    }
}

